/* @jsx h */
import { defineComponent, h, ref, watch } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { z } from "zod";
import type { GroupShop } from "@app/server/trpc/routers/shops";
import type { GroupIdentityListItem } from "@app/server/trpc/routers/identities";

export type GroupRole = "admin" | "viewer" | "employee";
export type PosRole = "staff" | "manager";
export type IdentityFormValue = {
  email: string;
  displayName: string;
  groupRole: GroupRole;
  grants: Array<{ restaurantId: number; role: PosRole }>;
  password?: string;
};

type IdentityFormProps = {
  mode: "create" | "edit";
  identity: GroupIdentityListItem | null;
  shops: GroupShop[];
  busy: boolean;
  onSubmit: (value: IdentityFormValue) => void | Promise<void>;
  onCancel: () => void;
  onRemove?: () => void | Promise<void>;
};

const formSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(320),
  displayName: z.string().trim().min(1).max(255),
  groupRole: z.enum(["admin", "viewer", "employee"]),
  grants: z.array(
    z.object({
      restaurantId: z.number().int().positive(),
      role: z.enum(["staff", "manager"]),
    }),
  ),
});

export const IdentityForm = defineComponent<IdentityFormProps>({
  name: "IdentityForm",
  props: [
    "mode",
    "identity",
    "shops",
    "busy",
    "onSubmit",
    "onCancel",
    "onRemove",
  ],
  setup(props) {
    const { t } = useLocalization();
    const email = ref("");
    const displayName = ref("");
    const groupRole = ref<GroupRole>("employee");
    const grants = ref<Record<number, PosRole>>({});
    const password = ref("");
    const confirmPassword = ref("");
    const validationError = ref("");

    const reset = () => {
      const identity = props.identity;
      email.value = identity?.email ?? "";
      displayName.value = identity?.name ?? "";
      groupRole.value = identity?.group_role ?? "employee";
      grants.value = Object.fromEntries(
        (identity?.grants ?? []).map((grant) => [
          grant.restaurant_id,
          grant.pos_role ?? "manager",
        ]),
      );
      password.value = "";
      confirmPassword.value = "";
      validationError.value = "";
    };

    watch(() => [props.mode, props.identity?.id, props.shops.length], reset, {
      immediate: true,
    });

    const toggleGrant = (restaurantId: number, enabled: boolean) => {
      const next = { ...grants.value };
      if (enabled) next[restaurantId] = next[restaurantId] ?? "staff";
      else delete next[restaurantId];
      grants.value = next;
    };

    const submit = async () => {
      const selectedGrants = Object.entries(grants.value).map(
        ([restaurantId, role]) => ({
          restaurantId: Number(restaurantId),
          role,
        }),
      );
      const parsed = formSchema.safeParse({
        email: email.value,
        displayName: displayName.value,
        groupRole: groupRole.value,
        grants: groupRole.value === "employee" ? selectedGrants : [],
      });

      if (
        !parsed.success ||
        (groupRole.value === "employee" && selectedGrants.length === 0) ||
        (props.mode === "create" &&
          (password.value.length < 8 ||
            password.value.length > 200 ||
            password.value !== confirmPassword.value))
      ) {
        validationError.value =
          props.mode === "create" && password.value !== confirmPassword.value
            ? t("admin.users.password_mismatch")
            : t("admin.users.validation_error");
        return;
      }

      validationError.value = "";
      await props.onSubmit({
        ...parsed.data,
        password: props.mode === "create" ? password.value : undefined,
      });
    };

    return () => (
      <form
        class="flex min-h-full flex-col"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <div class="flex-1 space-y-5 p-5">
          {validationError.value && (
            <div class="alert alert-error py-2 text-sm">
              <span class="material-symbols-outlined">error</span>
              <span>{validationError.value}</span>
            </div>
          )}

          <label class="form-control w-full">
            <span class="label-text mb-2">{t("admin.users.email")}</span>
            <input
              class="input input-bordered w-full"
              type="email"
              autocomplete="email"
              required
              disabled={props.mode === "edit" || props.busy}
              value={email.value}
              onInput={(event) => {
                email.value = (event.target as HTMLInputElement).value;
              }}
            />
          </label>

          <label class="form-control w-full">
            <span class="label-text mb-2">{t("admin.users.display_name")}</span>
            <input
              class="input input-bordered w-full"
              type="text"
              maxlength="255"
              required
              disabled={props.busy}
              value={displayName.value}
              onInput={(event) => {
                displayName.value = (event.target as HTMLInputElement).value;
              }}
            />
            <span class="mt-1 text-xs text-base-content/60">
              {t("admin.users.display_name_hint")}
            </span>
          </label>

          {props.mode === "create" && (
            <div class="grid gap-4 sm:grid-cols-2">
              <label class="form-control w-full">
                <span class="label-text mb-2">
                  {t("admin.users.initial_password")}
                </span>
                <input
                  class="input input-bordered w-full"
                  type="password"
                  autocomplete="new-password"
                  minlength="8"
                  maxlength="200"
                  required
                  disabled={props.busy}
                  value={password.value}
                  onInput={(event) => {
                    password.value = (event.target as HTMLInputElement).value;
                  }}
                />
              </label>
              <label class="form-control w-full">
                <span class="label-text mb-2">
                  {t("admin.users.confirm_password")}
                </span>
                <input
                  class="input input-bordered w-full"
                  type="password"
                  autocomplete="new-password"
                  minlength="8"
                  maxlength="200"
                  required
                  disabled={props.busy}
                  value={confirmPassword.value}
                  onInput={(event) => {
                    confirmPassword.value = (
                      event.target as HTMLInputElement
                    ).value;
                  }}
                />
              </label>
            </div>
          )}

          <label class="form-control w-full">
            <span class="label-text mb-2">{t("admin.users.group_role")}</span>
            <select
              class="select select-bordered w-full"
              disabled={props.busy}
              value={groupRole.value}
              onChange={(event) => {
                groupRole.value = (event.target as HTMLSelectElement)
                  .value as GroupRole;
              }}
            >
              {(["employee", "viewer", "admin"] as GroupRole[]).map((role) => (
                <option key={role} value={role}>
                  {t(`admin.roles.group_${role}`)}
                </option>
              ))}
            </select>
          </label>

          {groupRole.value === "employee" ? (
            <fieldset class="space-y-3">
              <legend class="font-semibold">
                {t("admin.users.shop_grants")}
              </legend>
              <p class="text-sm text-base-content/60">
                {t("admin.users.shop_grants_hint")}
              </p>
              {props.shops.length === 0 ? (
                <div class="rounded-xl border border-base-300 bg-base-200 p-4 text-sm text-base-content/70">
                  {t("admin.users.no_shops")}
                </div>
              ) : (
                props.shops.map((shop) => {
                  const role = grants.value[shop.id];
                  return (
                    <div
                      key={shop.id}
                      class="rounded-xl border border-base-300 bg-base-100 p-4"
                    >
                      <label class="flex cursor-pointer items-center gap-3">
                        <input
                          class="toggle toggle-primary toggle-sm"
                          type="checkbox"
                          disabled={props.busy}
                          checked={Boolean(role)}
                          onChange={(event) =>
                            toggleGrant(
                              shop.id,
                              (event.target as HTMLInputElement).checked,
                            )
                          }
                        />
                        <span class="min-w-0 flex-1">
                          <span class="block truncate font-medium">
                            {shop.name}
                          </span>
                          <span class="block truncate text-xs text-base-content/60">
                            {shop.address}
                          </span>
                        </span>
                      </label>
                      {role && (
                        <label class="mt-3 flex items-center gap-3 border-t border-base-300 pt-3">
                          <span class="flex-1 text-sm">
                            {t("admin.users.pos_role")}
                          </span>
                          <select
                            class="select select-bordered select-sm"
                            disabled={props.busy}
                            value={role}
                            onChange={(event) => {
                              grants.value = {
                                ...grants.value,
                                [shop.id]: (event.target as HTMLSelectElement)
                                  .value as PosRole,
                              };
                            }}
                          >
                            {(["staff", "manager"] as PosRole[]).map(
                              (posRole) => (
                                <option key={posRole} value={posRole}>
                                  {t(`admin.roles.pos_${posRole}`)}
                                </option>
                              ),
                            )}
                          </select>
                        </label>
                      )}
                    </div>
                  );
                })
              )}
            </fieldset>
          ) : (
            <div class="alert">
              <span class="material-symbols-outlined">info</span>
              <span>
                {groupRole.value === "admin"
                  ? t("admin.users.admin_access_hint")
                  : t("admin.users.viewer_access_hint")}
              </span>
            </div>
          )}

          {props.mode === "edit" && props.onRemove && (
            <div class="border-t border-base-300 pt-5">
              <button
                class="btn btn-error btn-outline btn-sm w-full"
                type="button"
                disabled={props.busy}
                onClick={() => {
                  if (window.confirm(t("admin.users.remove_confirm"))) {
                    void props.onRemove?.();
                  }
                }}
              >
                <span class="material-symbols-outlined text-lg">
                  person_remove
                </span>
                {t("admin.users.remove")}
              </button>
            </div>
          )}
        </div>

        <div class="sticky bottom-0 flex gap-3 border-t border-base-300 bg-base-100 p-4">
          <button
            class="btn btn-ghost flex-1"
            type="button"
            disabled={props.busy}
            onClick={props.onCancel}
          >
            {t("admin.common.cancel")}
          </button>
          <button
            class="btn btn-primary flex-1"
            type="submit"
            disabled={props.busy}
          >
            {props.busy && <span class="loading loading-spinner loading-sm" />}
            {props.mode === "create"
              ? t("admin.users.add_user")
              : t("admin.common.save")}
          </button>
        </div>
      </form>
    );
  },
});
